<template>
  <div>
    <!-- Data subjects table -->
    <div class="card"
         v-if="isGranted([permissions.ACTIVITY_DATA_SUBJECT_VIEW, permissions.ACTIVITY_DATA_SUBJECT_VIEW_OWN], activity)"
         qid="data-subjects-section"
         id="update_data_subject"
    >
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">

              <h5 class="mb-2" qid="data-subject-table-title" v-if="!showOnTask">
                {{ $t('activities.data_subject.title') }}
              </h5>

              <span v-if="!showOnTask">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_data_subject')"
                >
                  <UpdateSingleTaskModal
                      :task="getTask('update_data_subject')"
                      @reloadContent="reloadContent"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_data_subject')">
                  <GuideButton :guide="getGuide('update_data_subject')"></GuideButton>
                </span>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <span class="btn btn-success ml-3 pl-3 pr-3 cursor-pointer"
                  @click="showCreateDSModal()"
                  qid="data-subject-create-button"
                  v-if="isGranted([permissions.ACTIVITY_DATA_SUBJECT_CREATE, permissions.ACTIVITY_DATA_SUBJECT_CREATE_OWN], activity)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('activities.data_subject.add_new') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.data_subject.table.name') }}
            </th>
            <th scope="col" class="text-center">
              {{ $t('activities.data_subject.table.subjects') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="130"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="activityDataSubject in acTableItems">
              <td>
                <span v-if="activityDataSubject.data_subject && activityDataSubject.data_subject.name"
                      @click="openViewDataModal(activityDataSubject)"
                      class="btn btn-link cursor-pointer text-left"
                >
                  {{ activityDataSubject.data_subject.name }}
                </span>
              </td>
              <td class="text-center">
                <span v-if="activityDataSubject.data_subject && activityDataSubject.data_subject.number_of_subjects" class="text-normal">
                  {{ activityDataSubject.data_subject.number_of_subjects }}
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ activityDataSubject.updated_at | formatDate }}
                </span>
              </td>
              <td class="text-right">
                <span v-if="activityDataSubject.data_subject && activityDataSubject.data_subject.name"
                      @click="openViewDataModal(activityDataSubject)"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>

                <span class="btn btn-delete"
                        qid="delete-ds-modal"
                        @click="showDSConfirmDeleteModal(activityDataSubject)"
                        v-if="isGranted([permissions.ACTIVITY_DATA_SUBJECT_DELETE, permissions.ACTIVITY_DATA_SUBJECT_DELETE_OWN], activity)"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
              </td>
            </tr>
            <tr v-if="!acLoading && !acTableItems.length" qid="no-results-row-ds" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- Delete data subject modal template -->
    <b-modal ref="delete-ac" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ds-title">
          {{ $t('activities.data_subject.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.data_subject.delete.description') }}
        <span v-if="itemToDelete.data_subject">{{ itemToDelete.data_subject.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.data_subject.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideDSConfirmDeleteModal"
                  qid="delete-ds-cancel-button"
        >
          {{ $t('activities.data_subject.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteDS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ds-submit-button"
        >
          {{ $t('activities.data_subject.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new data subject -->
    <b-modal ref="add-data-subject" hide-footer @hide="hideCreateDSModal">
      <div class="d-block text-center">
        <h4 qid="add-ds-title">
          {{ $t('activities.data_subject.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="acFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('activities.data_subject.create.ds_label') }}
            </label>
            <multiselect
                v-model="selectedDS"
                :options="dsItems"
                :placeholder="$t('activities.data_subject.create.ds_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadDS"
                @select="resetFields"
                track-by="slug"
                qid="create-ds-form-ds"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedDS && selectedDS.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showDSDescription = !showDSDescription"
                    qid="expand-data-subject-description"
              >
                {{$t('activities.data_subject.create.ds_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showDSDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showDSDescription"/>
            </span>
          </div>
          <div v-if="selectedDS && selectedDS.description && showDSDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedDS.description }}
            </span>
          </div>
          <div v-if="selectedDS && selectedDS.number_of_subjects">
            <label class="mt-3">
                {{ $t('activities.data_subject.create.subjects_label') }}
            </label>
            <br>
            <span v-if="selectedDS">
              {{ selectedDS.number_of_subjects }}
            </span>
          </div>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateDSModal"
                    qid="create-ds-cancel-button"
          >
            {{ $t('activities.data_subject.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="create-ds-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.data_subject.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <DataSubjectViewModal
        :object="selectedDataSubject"
        :showModal="viewDataSubjectModal"
        @closeViewModal="handleCloseViewDataModal"
    ></DataSubjectViewModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import DataSubjectViewModal from '@/views/Activities/DataViewModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';

  export default {
    name: 'DataSubjectsTable',
    data: function() {
      return {
        acForm: {
          name: '',
          provisioning: '',
          re_certification: ''
        },
        error: '',
        loadingItems: false,
        selectedDS: [],
        selectedDSSort: '',
        selectedDSDirection: '',
        acValidationShow: false,
        acTableItems: [],
        acLoading: true,
        dsItems: [],
        updateValidationList: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: {},
        disabledValidationOptions: [],
        showDSDescription: false,
        buttonDisabled: false,
        selectedDataSubject: {},
        viewDataSubjectModal: false,
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      ValidationList,
      ValidationForm,
      DataSubjectViewModal,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadDS();
      },
      object: function (value) {
        this.activity = value
        this.loadTasks('update_data_subject');
        this.loadDataSubjects();
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
              this.activity = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_data_subject')
        }
      },
    },
    methods: {
      resetFields() {
        this.selectedRC = null
        this.selectedP = null
      },
      reloadContent() {
        this.loadDataSubjects()
        this.loadTasks('update_data_subject')
      },
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.activity.who_am_i,
              model_id: this.activity.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      handleCloseViewDataModal () {
        this.viewDataSubjectModal = false;
      },
      openViewDataModal(object) {
        this.selectedDataSubject = object
        this.viewDataSubjectModal = true
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Activity' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Activity',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      setValidation(object) {
        this.validation = object
      },
      showCreateDSModal() {
        this.$refs['add-data-subject'].show()
      },
      hideCreateDSModal() {
        this.error = ''
        this.selectedDS = []
        this.loadContent()
        this.$refs['add-data-subject'].hide()
      },
      showDSConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-ac'].show()
      },
      hideDSConfirmDeleteModal() {
        this.itemToDelete = {}
        this.loadContent()
        this.$refs['delete-ac'].hide()
      },
      handleValidationModalClose() {
        this.updateValidationList = true
        this.loadContent()
      },
      loadContent() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.activity = response.data;
        }, function() {});
      },
      loadDataSubjects() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug + '/data-subjects', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.acTableItems = response.data;
          self.acLoading = false;
        }, function() {});
      },
      confirmDeleteDS(object) {
        let self = this;
        piincHttp.delete('activities/' + this.activity.slug + '/data-subjects/' + object.id).then(function() {
          self.hideDSConfirmDeleteModal();
          self.loadDataSubjects();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.data_subject.delete.success'))
        }, function() {});
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      acFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedDS && this.selectedDS.id) {
          formData.append("data_subject_id", this.selectedDS.id);
        }

        formData.append("activity", this.activity.slug);

        piincHttp.post('/activities/' + this.activity.slug + '/data-subjects', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('activities.data_subject.create.success'))
          self.hideCreateDSModal();
          self.loadDataSubjects();
          self.loadTasks('update_data_subject');
          self.$emit('updated');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.data_subject_id) {
              self.error += ' ' + error.data.errors.data_subject_id[0]
            }
          }
        })
      },
      loadDS(query) {
        let self = this;
        piincHttp.get('data-subjects', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.dsItems = response.data.items
          if (self.dsItems && !self.dsItems.length && !query) {
            self.dsItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
          if (self.dsItems && self.dsItems.length) {
            self.provisioningOptions = self.dsItems[0].provisioningOptions
            self.reCertificationOptions = self.dsItems[0].reCertificationOptions
            self.validationOptions = _.cloneDeep(self.dsItems[0].validationOptions)
          }
        }, function() {});
      },
    },
    created () {
      this.activity = this.object;
      if (this.getClient && this.getClient.slug) {
        this.loadDS();
      }

      if (this.activity && this.activity.slug) {
        this.loadTasks('update_data_subject');
        this.loadDataSubjects();
      }

      this.loadGuides('update_data_subject');

    }
  }
</script>
